import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { InternalTrainingProgram, SelectionCriteria, AssignmentType } from "@/types/internalTraining";

type ProgramStatus = 'draft' | 'active' | 'paused' | 'completed';

interface ProgramInput {
  name: string;
  description?: string;
  status?: ProgramStatus;
  selection_criteria?: SelectionCriteria;
  auto_assign_enabled?: boolean;
  assignment_type?: AssignmentType;
  metadata?: Record<string, any>;
}

export const useInternalTrainingPrograms = (brandId: string | null) => {
  const [programs, setPrograms] = useState<InternalTrainingProgram[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchPrograms = async () => {
    if (!brandId) {
      setPrograms([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('os_internal_training_programs')
        .select('*')
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const typedData = (data || []).map(item => ({
        ...item,
        status: item.status as ProgramStatus
      })) as InternalTrainingProgram[];

      setPrograms(typedData);
    } catch (error) {
      console.error('Error fetching internal training programs:', error);
      setPrograms([]);
    } finally {
      setIsLoading(false);
    }
  };

  const createProgram = async (programData: ProgramInput): Promise<InternalTrainingProgram | null> => {
    if (!brandId) return null;

    setIsCreating(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('os_internal_training_programs')
        .insert({
          brand_id: brandId,
          user_id: userData.user?.id,
          name: programData.name,
          description: programData.description,
          status: programData.status || 'draft',
          metadata: programData.metadata,
        })
        .select()
        .single();

      if (error) throw error;

      const typedData = { ...data, status: data.status as ProgramStatus } as InternalTrainingProgram;
      setPrograms(prev => [typedData, ...prev]);
      return typedData;
    } catch (error) {
      console.error('Error creating internal training program:', error);
      return null;
    } finally {
      setIsCreating(false);
    }
  };

  const updateProgram = async (id: string, programData: Partial<ProgramInput>): Promise<InternalTrainingProgram | null> => {
    setIsUpdating(true);
    try {
      const updateData: Record<string, any> = {
        ...programData,
        updated_at: new Date().toISOString(),
      };
      
      // Cast selection_criteria to Json for Supabase
      if (programData.selection_criteria) {
        updateData.selection_criteria = programData.selection_criteria as Record<string, any>;
      }
      
      const { data, error } = await supabase
        .from('os_internal_training_programs')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      const typedData = { ...data, status: data.status as ProgramStatus } as InternalTrainingProgram;
      setPrograms(prev => prev.map(p => p.id === id ? typedData : p));
      return typedData;
    } catch (error) {
      console.error('Error updating internal training program:', error);
      return null;
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteProgram = async (id: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('os_internal_training_programs')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setPrograms(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error deleting internal training program:', error);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, [brandId]);

  return {
    programs,
    isLoading,
    isCreating,
    isUpdating,
    refetch: fetchPrograms,
    createProgram,
    updateProgram,
    deleteProgram,
  };
};
